import { useMemo, useState } from 'react'
import { PowerPerformanceForms, ToggleLabel, UserInvestmentType } from '../../types/mutual-fund-detail.types'
import { ReasonsBanner, ReasonsBannerType } from './ReasonsBanner'
import { StrategyBanner, StrategyBannerType } from './StrategyBanner'
import { KnowWhyAPIReponseType } from '../../types/mutual-fund-detail.types'
import { toPascalCaseWithSpaces } from '../../utils/utils'
import { EliteBanner } from './EliteBanner'
import { KnowWhyExploreFund } from './KnowWhyExploreFund'
import RenderHTML from '../ui-components/RenderHTML'
import { useScaleAnimation } from '../../hooks/useScaleAnimation'
import { trackEvent } from '../../utils/appBridge'
import { EventNames } from '../../types/misc.types'
import { PowerRankSection } from './score-sections/PowerRankSection'
import { ConsistencyScoreSection } from './score-sections/ConsistencyScoreSection'
import { RecencyScoreSection } from './score-sections/RecencyScoreSection'
import { VolatilityScoreSection } from './score-sections/VolatilityScoreSection'
import { getScoreSections, ScoreSectionType } from './utils'

export const useBuildKnowWhyBottomSheetContent = ({
  powerForm,
  userInvestmentType,
  knowWhyData,
  ScreenName
}: {
  powerForm: `${PowerPerformanceForms}`
  userInvestmentType: `${UserInvestmentType}`
  knowWhyData: KnowWhyAPIReponseType
  ScreenName: string
}) => {
  const reasonsBanner = useMemo(() => {
    switch (powerForm) {
      case PowerPerformanceForms.IN_FORM: {
        return <ReasonsBanner bannerType={ReasonsBannerType.REASONS_IN_FORM} />
      }
      case PowerPerformanceForms.ON_TRACK: {
        return <ReasonsBanner bannerType={ReasonsBannerType.REASONS_ON_TRACK} />
      }
      case PowerPerformanceForms.OFF_TRACK: {
        return <ReasonsBanner bannerType={ReasonsBannerType.REASONS_OFF_TRACK} />
      }
      case PowerPerformanceForms.OUT_OF_FORM: {
        return <ReasonsBanner bannerType={ReasonsBannerType.REASONS_OUT_OF_FORM} />
      }
    }
  }, [powerForm, userInvestmentType])

  const recommendationsBanner = useMemo(() => {
    switch (powerForm) {
      case PowerPerformanceForms.IN_FORM: {
        return <StrategyBanner bannerType={StrategyBannerType.RECOMMENDATIONS_IN_FORM} />
      }
      case PowerPerformanceForms.ON_TRACK: {
        return <StrategyBanner bannerType={StrategyBannerType.RECOMMENDATIONS_ON_TRACK} />
      }
      case PowerPerformanceForms.OFF_TRACK: {
        return null
      }
      case PowerPerformanceForms.OUT_OF_FORM: {
        return null
      }
    }
  }, [powerForm, userInvestmentType])

  const nextStepsBanner = useMemo(() => {
    switch (powerForm) {
      case PowerPerformanceForms.IN_FORM: {
        return null
      }
      case PowerPerformanceForms.ON_TRACK: {
        return null
      }
      case PowerPerformanceForms.OFF_TRACK: {
        return (
          <NextStepsSection
            knowWhyData={knowWhyData}
            investmentType={userInvestmentType}
            powerForm={powerForm}
            ScreenName={ScreenName}
          />
        )
      }
      case PowerPerformanceForms.OUT_OF_FORM: {
        return (
          <NextStepsSection
            knowWhyData={knowWhyData}
            investmentType={userInvestmentType}
            powerForm={powerForm}
            ScreenName={ScreenName}
          />
        )
      }
    }
  }, [powerForm, userInvestmentType, knowWhyData])

  const recommendationPoints = useMemo(() => {
    switch (powerForm) {
      case PowerPerformanceForms.IN_FORM: {
        return [
          {
            label: `<span style="font-weight: 500;"> Great choice for SIP </span> in ${toPascalCaseWithSpaces(knowWhyData?.category ?? '')} funds`
          },
          {
            label: `<span style="font-weight: 500;"> Great choice for lumpsum investing </span> in ${toPascalCaseWithSpaces(knowWhyData?.category ?? '')} funds`
          }
        ]
      }
      case PowerPerformanceForms.ON_TRACK: {
        return [
          {
            label: `<span style="font-weight: 500;"> Good choice for SIP </span> in ${toPascalCaseWithSpaces(knowWhyData?.category ?? '')} funds`
          },
          {
            label: `<span style="font-weight: 500;"> Good choice for lumpsum investing </span> in ${toPascalCaseWithSpaces(knowWhyData?.category ?? '')} funds`
          }
        ]
      }
      case PowerPerformanceForms.OFF_TRACK: {
        return [
          {
            label: `<span style="font-weight: 500;"> Don't start a new SIP </span> in this fund right now`
          },
          {
            label: `<span style="font-weight: 500;"> Don't make a lumpsum investment </span> in this fund right now`
          }
        ]
      }
      case PowerPerformanceForms.OUT_OF_FORM: {
        return [
          {
            label: `<span style="font-weight: 500;"> Don't start a new SIP </span> in this fund right now`
          },
          {
            label: `<span style="font-weight: 500;"> Don't make a lumpsum investment </span> in this fund right now`
          }
        ]
      }
    }
  }, [powerForm, knowWhyData?.category])

  const bottomCopy = useMemo(() => {
    switch (powerForm) {
      case PowerPerformanceForms.IN_FORM: {
        return `76% of PowerUp users increased investments in their in-form funds in last 3 months`
      }
      case PowerPerformanceForms.ON_TRACK: {
        return `72% of PowerUp users increased investments in their on-track funds in the last 3 months`
      }
      case PowerPerformanceForms.OFF_TRACK: {
        if (userInvestmentType === UserInvestmentType.SIP) {
          return `Nearly 70% <span style="font-weight: 400;"> of users with SIPs in off-track funds have paused them and switched to stronger funds</span>`
        } else if (userInvestmentType === UserInvestmentType.LUMPSUM) {
          return `Over 80% of users haven't added more or exited their Off track funds`
        }
        return `Nearly 70% <span style="font-weight: 400;"> of users with SIPs in off-track funds have paused them and switched to stronger funds</span>`
      }
      case PowerPerformanceForms.OUT_OF_FORM: {
        return `98% <span style="font-weight: 400;"> of PowerUp users have exited their out-of-form funds in last 3 months</span>`
      }
    }
  }, [powerForm])

  const getScoreSection = (sectionType: ScoreSectionType, index: number, listLength: number) => {
    const common = {
      knowWhyData,
      isElite: knowWhyData?.isElite ?? false,
      number: index + 1,
      isLastSection: index === listLength - 1
    }
    switch (sectionType) {
      case ScoreSectionType.POWER_RANK: {
        return <PowerRankSection key={sectionType} {...common} />
      }
      case ScoreSectionType.CONSISTENCY_SCORE: {
        return <ConsistencyScoreSection key={sectionType} {...common} />
      }
      case ScoreSectionType.RECENCY_SCORE: {
        return <RecencyScoreSection key={sectionType} {...common} />
      }
      case ScoreSectionType.VOLATILITY_SCORE: {
        return <VolatilityScoreSection key={sectionType} {...common} />
      }
      default: {
        return null
      }
    }
  }

  const scoreSections = useMemo(() => {
    const filteredSections = getScoreSections(knowWhyData)
    if (!filteredSections?.length) return null
    const listLength = filteredSections.length
    return (
      <ul aria-label='Performance scores' className='list-none m-0 p-0 flex flex-col'>
        {filteredSections.map((sectionType, index) => getScoreSection(sectionType, index, listLength))}
      </ul>
    )
  }, [knowWhyData])

  return {
    reasonsBanner,
    recommendationsBanner,
    nextStepsBanner,
    recommendationPoints,
    bottomCopy,
    scoreSections
  }
}

const toogleConfig = [
  {
    tenureToggles: ['1Y', '3Y', '5Y'],
    togglelabel: ToggleLabel.ROLLING_RETURNS
  },
  {
    tenureToggles: ['1Y', '3Y', '5Y'],
    togglelabel: ToggleLabel.TRAILING_RETURNS
  },
  {
    tenureToggles: [],
    togglelabel: ToggleLabel.AUM
  }
]

const ToggleIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="5" height="12" viewBox="0 0 5 12" fill="none">
  <path fill-rule="evenodd" clip-rule="evenodd" d="M2.85355 1.66476C2.65829 1.44508 2.34171 1.44508 2.14645 1.66476L0.146463 3.91476C0.00346387 4.07563 -0.0393159 4.31756 0.0380737 4.52776C0.115463 4.73795 0.297782 4.875 0.500011 4.875H4.49999C4.70222 4.875 4.88454 4.73795 4.96193 4.52776C5.03932 4.31756 4.99654 4.07563 4.85354 3.91476L2.85355 1.66476ZM0.0380737 7.47224C0.115463 7.26205 0.297782 7.125 0.500011 7.125H4.49999C4.70222 7.125 4.88454 7.26205 4.96193 7.47224C5.03932 7.68243 4.99654 7.92437 4.85354 8.08524L2.85355 10.3353C2.65829 10.5549 2.34171 10.5549 2.14645 10.3353L0.146463 8.08524C0.00346387 7.92437 -0.0393159 7.68243 0.0380737 7.47224Z" fill="#232323"/>
</svg>`

// Move NextStepsSection to a separate component file
export const NextStepsSection = ({
  knowWhyData,
  investmentType,
  powerForm,
  ScreenName
}: {
  knowWhyData: KnowWhyAPIReponseType
  investmentType: `${UserInvestmentType}`
  powerForm: `${PowerPerformanceForms}`
  ScreenName: string
}) => {
  const [selectedToggle, setSelectedToggle] = useState<{
    tenureToggles: string[]
    togglelabel: `${ToggleLabel}`
  }>(toogleConfig?.[0])
  const [selectedTenureToggle, setSelectedTenureToggle] = useState<string>(toogleConfig?.[0]?.tenureToggles?.[0])
  const { scaleStyle: tenureScaleStyle, triggerAnimation: triggerTenureAnimation } = useScaleAnimation()
  const { scaleStyle: toggleScaleStyle, triggerAnimation: triggerToggleAnimation } = useScaleAnimation()

  const getNextTenureToggle = () => {
    triggerTenureAnimation()
    const currentToggle = toogleConfig?.find((toggle) => toggle?.togglelabel === selectedToggle?.togglelabel)
    const currentTenureIndex = currentToggle?.tenureToggles?.findIndex((tenure) => tenure === selectedTenureToggle)
    const nextTenureIndex =
      currentTenureIndex !== undefined && currentTenureIndex > -1
        ? (currentTenureIndex + 1) % (currentToggle?.tenureToggles?.length || 1)
        : 0
    setSelectedTenureToggle(currentToggle?.tenureToggles?.[nextTenureIndex] || '')
  }

  const getNextToggle = () => {
    triggerToggleAnimation()
    const currentIndex = toogleConfig?.findIndex((toggle) => toggle?.togglelabel === selectedToggle?.togglelabel)
    const nextIndex = (currentIndex + 1) % toogleConfig?.length
    setSelectedTenureToggle(toogleConfig?.[nextIndex]?.tenureToggles?.[0] || '')
    setSelectedToggle(toogleConfig?.[nextIndex])
  }

  const isNoSectionCase = useMemo(() => {
    return knowWhyData?.recommendedFunds && knowWhyData?.recommendedFunds?.length === 0
  }, [knowWhyData?.recommendedFunds])

  const nextStepsBannerType = useMemo(() => {
    return StrategyBannerType.NEXT_STEPS_IN_FORM
  }, [powerForm])

  if (isNoSectionCase) {
    return null
  }

  return (
    <div className='w-full flex flex-col'>
      <StrategyBanner bannerType={nextStepsBannerType} />
      {knowWhyData?.recommendedFunds && knowWhyData?.recommendedFunds?.length > 0 && (
        <div className='border-t border-[#F0F4F7] bg-white p-5 flex justify-between'>
          <div className='text-[#232323] text-[14px] font-[500]'>
            {knowWhyData?.recommendedFunds?.length} Fund{knowWhyData?.recommendedFunds?.length === 1 ? '' : 's'}
          </div>
          <div className='flex gap-2 items-center text-[#232323] text-[13px] font-[400]'>
            {selectedTenureToggle && (
              <>
                <div
                  onClick={getNextTenureToggle}
                  className='cursor-pointer flex items-center gap-1 pr-3'
                  style={tenureScaleStyle}
                >
                  {selectedTenureToggle} <RenderHTML innerHTMLText={ToggleIcon} />
                </div>
                <div className='w-[1px] bg-[#D1D6D6] h-3'></div>
              </>
            )}
            <div
              onClick={getNextToggle}
              className='cursor-pointer flex items-center gap-1 pl-2'
              style={toggleScaleStyle}
            >
              {selectedToggle?.togglelabel} <RenderHTML innerHTMLText={ToggleIcon} />
            </div>
          </div>
        </div>
      )}
      <div className='flex flex-col'>
        {knowWhyData?.recommendedFunds?.map((fund, idx) => {
          return (
            <div key={`${fund?.isin}-${idx}`} className='bg-white border-t border-[#F0F4F7] overflow-hidden'>
              <KnowWhyExploreFund
                logoSrc={fund?.logo}
                title={fund?.name}
                fundType={fund?.nature}
                fundCategory={fund?.category}
                formType={fund?.powerPerformanceData?.powerRating as `${PowerPerformanceForms}`}
                powerRank={fund?.powerPerformanceData?.powerRank}
                isElite={knowWhyData?.isElite}
                selectedToggle={selectedToggle}
                selectedTenureToggle={selectedTenureToggle}
                fund={fund}
              />
            </div>
          )
        })}
      </div>

      {!knowWhyData?.isElite && (
        <div className='pt-2 bg-white'>
          <EliteBanner
            onBannerClick={() => {
              trackEvent({
                eventName: EventNames.ButtonTap,
                payload: {
                  eventId: 'Upgrade to Elite_V2',
                  screenType: 'MF_Know_Why',
                  ScreenName
                }
              })
            }}
          />
        </div>
      )}
    </div>
  )
}
